import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParams, StringParam } from 'use-query-params';

import { getLatestKycProcess } from 'apis/legitimizer/kyc/api';
import { RouteWhenAuthenticated } from 'common/components';

import IdentityVerification from './IdentityVerification';
import KycProcessing from './KycProcessing';
import KycSuccess from './KycSuccess';
import RetryPage from './RetryPage';
import VerifyRedirect from './VerifyRedirect';

const IdentityVerificationContentRoot = styled.main`
  width: 100%;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;
  background-color: ${cssThemedValue('surfaceMain')};
`;

const SpinnerContainer = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IdentityVerificationRoutes = () => {
  const { path } = useRouteMatch();

  const [query] = useQueryParams({
    oidc_error: StringParam,
  });

  const { data: kycProcess = null, isLoading } = useQuery(['kyc/processes/latest'], () => getLatestKycProcess(), {
    retry: false,
  });

  if (isLoading) {
    return (
      <IdentityVerificationContentRoot>
        <SpinnerContainer>
          <Spinner size="extraLarge" />
        </SpinnerContainer>
      </IdentityVerificationContentRoot>
    );
  }

  return (
    <IdentityVerificationContentRoot>
      <Switch>
        <RouteWhenAuthenticated path={`${path}/redirect`} component={VerifyRedirect} />
        {(() => {
          switch (kycProcess?.state) {
            case 'OPEN':
              return (
                <Switch>
                  <RouteWhenAuthenticated path={`${path}/intro`} component={IdentityVerification} />
                  <RouteWhenAuthenticated path={`${path}/retry`} component={RetryPage} />
                  {kycProcess.attemptCount === 0 ? (
                    <Redirect
                      to={{
                        pathname: `${path}/intro`,
                        search: query.oidc_error ? `oidc_error=${query.oidc_error}` : '',
                      }}
                    />
                  ) : (
                    <Redirect to={`${path}/retry`} />
                  )}
                </Switch>
              );
            case 'PROCESSING':
              return (
                <Switch>
                  <RouteWhenAuthenticated path={`${path}/processing`} component={KycProcessing} />
                  <Redirect to={`${path}/processing`} />
                </Switch>
              );
            case 'COMPLETED':
              return <RouteWhenAuthenticated path={`${path}/success`} component={KycSuccess} />;
            // If no process is found
            default:
              return <Redirect to="/" />;
          }
        })()}
        <Redirect to="/" />;
      </Switch>
    </IdentityVerificationContentRoot>
  );
};

export default IdentityVerificationRoutes;
